// CSV export — serialise table data to a downloadable .csv file.
//
// Pure client-side: the rows are already in the React Query cache, so export
// needs no backend endpoint. bigint values (money in minor units — money is
// bigint end-to-end) are stringified, since CSV is text.
//
// NOTE: strata used papaparse for this; go-pim-ui dropped papaparse (and xlsx)
// to keep the bundle lean, so the tiny RFC-4180-style serializer below replaces
// Papa.unparse. It handles the only cases the panel produces: flat string /
// number / bigint / null cells.

/** Quote a single CSV cell per RFC 4180 when it contains a comma, quote, or newline. */
function csvCell(value: unknown): string {
  if (value == null) return "";
  const s = typeof value === "bigint" ? value.toString() : String(value);
  if (/[",\r\n]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

/**
 * unparse — turn an array of flat records into a CSV string. Column order
 * follows the keys of the first row.
 */
function unparse(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const lines = [headers.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => csvCell(row[h])).join(","));
  }
  return lines.join("\r\n");
}

/**
 * downloadCsv — turn an array of flat records into a CSV file and trigger a
 * browser download. Column order follows the keys of the first row.
 */
export function downloadCsv(
  filename: string,
  rows: Record<string, unknown>[],
): void {
  const csv = unparse(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveBlob(filename, blob);
}

/**
 * saveBlob — turn any Blob into a downloaded file. Create a temp Object URL,
 * click a hidden <a>, revoke. Browsers free the underlying blob storage when
 * the URL is revoked.
 */
export function saveBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
